// stores/roleModuleStore.js
'use client';

import FilterBuilder from '../utils/filterBuilder';
import { fleetApiGo, fleetApi } from '../api/axiosClient';

const getTzParam = () => `tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;
const appendTz = (url) => (url.includes('?') ? `${url}&${getTzParam()}` : `${url}?${getTzParam()}`);

export const getRolesModuleByName = async (roleName) => {
  if (!roleName) throw new Error('roleName is required');
  try {
    const response = await fleetApi.get(appendTz(`/roles-module-by-role?roleName=${roleName}`));
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch roles module for ${roleName}:`, error?.response?.data ?? error);
    throw error;
  }
};

export const getRolesCompanyList = async (roleName) => {
  if (!roleName) throw new Error('roleName is required');
  try {
    let allData = [];
    let start = 0;
    const length = 100;
    let hasMore = true;

    while (hasMore) {
      const url = `/roles-company/list?start=${start}&length=${length}&column=role_name&value=${encodeURIComponent(roleName)}`;
      const response = await fleetApi.get(appendTz(url));

      const resData = Array.isArray(response.data?.data) ? response.data.data : Array.isArray(response.data) ? response.data : [];
      allData = [...allData, ...resData];

      if (resData.length < length) {
        hasMore = false;
      } else {
        start += length;
      }
    }

    return { data: allData, totalRecord: allData.length };
  } catch (error) {
    console.error(`Failed to fetch roles company list for ${roleName}:`, error?.response?.data ?? error);
    throw error;
  }
};

export const getRolesVehicleList = async (roleName) => {
  if (!roleName) throw new Error('roleName is required');
  try {
    let allData = [];
    let start = 0;
    const length = 100;
    let hasMore = true;

    while (hasMore) {
      const url = `/roles-vehicle/list?start=${start}&length=${length}&column=role_name&value=${encodeURIComponent(roleName)}`;
      const response = await fleetApi.get(appendTz(url));

      const resData = Array.isArray(response.data?.data) ? response.data.data : Array.isArray(response.data) ? response.data : [];
      allData = [...allData, ...resData];

      if (resData.length < length) {
        hasMore = false;
      } else {
        start += length;
      }
    }

    return { data: allData, totalRecord: allData.length };
  } catch (error) {
    console.error(`Failed to fetch roles vehicle list for ${roleName}:`, error?.response?.data ?? error);
    throw error;
  }
};

export const searchVehicleForRoles = async (companyIds) => {
  try {
    const idStr = Array.isArray(companyIds) ? companyIds.join(',') : String(companyIds ?? '');

    const fb = new FilterBuilder().addJoin('company', ['name']).addJoin('vehicle_type', ['name']);
    const payload = fb.build();
    payload.paging = { length: -1 };

    if (idStr && idStr.trim() !== '') {
      payload.columns = [
        {
          name: 'company_id',
          logic_operator: 'in',
          value: idStr,
          value1: '',
          operator: 'AND'
        }
      ];
    }

    const response = await fleetApiGo.post(appendTz('/vehicle/search-vehicle'), payload);
    return response.data;
  } catch (error) {
    console.error('Failed to search vehicle for roles:', error?.response?.data ?? error);
    throw error;
  }
};

export const getModuleList = async () => {
  try {
    const response = await fleetApi.get(appendTz('/master-menu'));
    return response.data;
  } catch (error) {
    console.error('Failed to fetch module list:', error?.response?.data ?? error);
    throw error;
  }
};

export const createModuleMenu = async (payload) => {
  try {
    const response = await fleetApi.post(appendTz('/modules/new'), payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    return response.data;
  } catch (error) {
    console.error('Failed to create module menu:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateModuleMenu = async (payload) => {
  if (!payload?.id) throw new Error('Module ID is required in payload for update');
  try {
    const response = await fleetApi.put(appendTz(`/modules/edit/${payload.id}`), payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    return response.data;
  } catch (error) {
    console.error(`Failed to update module menu ${payload.id}:`, error?.response?.data ?? error);
    throw error;
  }
};

export const updateModuleMenuAll = async (payload) => {
  try {
    const response = await fleetApi.put(appendTz('/modules/edit-all'), payload);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    return response.data;
  } catch (error) {
    console.error('Failed to update all module menus:', error?.response?.data ?? error);
    throw error;
  }
};

export const deleteModuleMenu = async (id) => {
  if (!id) throw new Error('Module ID is required for deletion');
  try {
    const response = await fleetApi.delete(appendTz(`/modules/delete/${id}`));
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    return response.data;
  } catch (error) {
    console.error(`Failed to delete module menu ${id}:`, error?.response?.data ?? error);
    throw error;
  }
};
